import React, { useEffect, useMemo, useRef } from 'react';

import { Polygon as GPolygon } from '@react-google-maps/api';
import * as wicket from 'wicket';

import { IMapShapeProps } from '../types';

const Wkt = wicket.Wkt;
export const MAX_POINTS = 250;

export const serialize = (polygon: google.maps.Polygon) => {
  const wkt = new Wkt();
  wkt.fromObject(polygon);
  return wkt.write();
};

export const getPointCount = (polygon: google.maps.Polygon) => {
  const pointCount = polygon
    .getPaths()
    .getArray()
    .reduce((sum, path) => sum + path.getLength(), 0);

  return pointCount;
};

export const getTotalPointCount = (polygons: Array<google.maps.Polygon | null | undefined>) =>
  polygons.reduce((sum, polygon) => sum + (polygon ? getPointCount(polygon) : 0), 0);

export const parse = (wellKnownText: string) => {
  const wkt = new Wkt();
  wkt.read(wellKnownText);
  return wkt.toObject() as google.maps.Polygon;
};

export const isEqual = (a: google.maps.Polygon, b: google.maps.Polygon) => {
  if (a.getPaths().getLength() !== b.getPaths().getLength()) {
    return false;
  }
  return a
    .getPaths()
    .getArray()
    .every((mvca, mvcaIdx) => {
      if (mvca.getArray().length !== b.getPaths().getAt(mvcaIdx).getArray().length) {
        return false;
      }
      return mvca.getArray().every((l, i) => l.equals(b.getPaths().getAt(mvcaIdx).getAt(i)));
    });
};

export const getDefaultPolygon = (center: google.maps.LatLng, distance: number) => {
  const polygon = new google.maps.Polygon();
  polygon.setPaths([
    [
      // compute ne point
      google.maps.geometry.spherical.computeOffset(center, distance, 45),
      // compute se point
      google.maps.geometry.spherical.computeOffset(center, distance, 135),
      // compute sw point
      google.maps.geometry.spherical.computeOffset(center, distance, 225),
      // compute nw point
      google.maps.geometry.spherical.computeOffset(center, distance, 315),
    ],
  ]);
  return serialize(polygon);
};

const Polygon = (props: IMapShapeProps) => {
  const { zone, onSelect, disabled, onChange, onPointCapChange, onPointCapReached } = props;

  const { polygon: initialPolygon, initPolygon } = useMemo(() => {
    try {
      return {
        polygon: parse(zone.WellKnownText as string), // !!!MUTABLE!!!
        initPolygon: zone.wkt.toObject() as google.maps.Polygon,
      };
    } catch (error) {
      console.error(error);
      return {
        polygon: undefined,
        initPolygon: undefined,
      };
    }
  }, [zone]);

  const polygonRef = useRef(initialPolygon);
  const previousPointCountRef = useRef(0);

  useEffect(() => {
    if (!polygonRef.current || disabled) {
      previousPointCountRef.current = 0;
      onPointCapChange(false);
      return;
    }

    if (!zone.selected) {
      return;
    }

    const pointCount = getPointCount(polygonRef.current);
    previousPointCountRef.current = pointCount;
    onPointCapChange(pointCount >= MAX_POINTS);
  }, [disabled, onPointCapChange, zone.selected, zone.WellKnownText]);

  if (!polygonRef.current || !initPolygon) {
    return null;
  }

  return (
    <GPolygon
      key={zone.Id as number}
      onClick={disabled ? undefined : onSelect}
      onMouseUp={
        disabled
          ? undefined
          : () => {
              if (polygonRef.current && !isEqual(polygonRef.current, initPolygon)) {
                const pointCount = getPointCount(polygonRef.current);
                if (previousPointCountRef.current < MAX_POINTS && pointCount >= MAX_POINTS) {
                  onPointCapReached();
                }

                previousPointCountRef.current = pointCount;
                onPointCapChange(pointCount >= MAX_POINTS);
                onChange(serialize(polygonRef.current));
              }
            }
      }
      paths={polygonRef.current.getPaths()}
      options={zone.options}
    />
  );
};

export default Polygon;
